export class ApiError extends Error {
	status: number;
	body: unknown;
	constructor(message: string, status: number, body?: unknown) {
		super(message);
		this.status = status;
		this.body = body;
	}
}

export type MeResponse = {
	userId: string;
	inCouple: boolean;
	couple: {
		id: string;
		createdAt: number;
		currentStreak: number;
		bestStreak: number;
		partnerJoined: boolean;
		paired: boolean;
		code: string | null;
		partner: string | null;
		clerkUserId: string;
		user1: { nickname: string; name: string; gender: string | null; birthDate: string | null; adultConsent: boolean | null; clerkUserId: string };
		user2?: { nickname: string; name: string; gender: string | null; birthDate: string | null; adultConsent: boolean | null; clerkUserId: string };
	} | null;
	profile: {
		nickname: string | null;
		gender: string | null;
		birthDate: string | null;
		adultConsent: boolean | null;
	} | null;
};

export type Gender = "male" | "female" | "other" | "prefer_not_say";

export type ProfileInput = {
	nickname: string;
	gender: Gender;
	birthDate: string;
	adultConsent: boolean;
};

export type TodayQuiz = {
	coupleId: string;
	date: string;
	category: string;
	questions: Array<{
		id: string;
		position: number;
		category: string;
		questionText: string;
		optionA: string;
		optionB: string;
		optionC: string;
		optionD: string;
	}>;
	myAnswerByQuestion: Record<string, "a" | "b" | "c" | "d" | undefined>;
	myAnsweredCount: number;
	partnerAnsweredCount: number;
	totalQuestions: number;
	bothAnswered: boolean;
	streakDays: number;
};

export type TodayResultQuestion = {
	position: number;
	questionText: string;
	optionA: string;
	optionB: string;
	optionC: string;
	optionD: string;
	user1Answer: string | null;
	user2Answer: string | null;
	matched: boolean;
};

export type Tier = {
	slug: string;
	label: string;
	color: string;
	gaugePercent: number;
};

export type TodayResult =
	| { available: false; bothAnswered: false; lastRemindSentAt: number | null }
	| {
			available: true;
			bothAnswered: boolean;
			coupleId: string;
			date: string;
			category: string | null;
			totalQuestions: number;
			matchCount: number;
			myAnsweredCount: number;
			partnerAnsweredCount: number;
			tier: Tier;
			questionResults: TodayResultQuestion[];
			lastRemindSentAt: number | null;
			recap: string | null;
	  };

export type Streak = {
	currentStreakDays: number;
	bestStreakDays: number;
	lastBothAnsweredDate: string | null;
	flame: { icon: string; color: string; label: string; intensity: string };
	dailyResults: Record<
		string,
		{ tier: string; matchCount: number; bothAnswered: boolean }
	>;
};

export type HistoryEntry = {
	date: string;
	category: string;
	matchCount: number;
	totalQuestions: number;
	tierSlug: string;
	tierLabel: string;
	bothAnswered: boolean;
};

export type HistoryDetail = {
	available: true;
	bothAnswered: boolean;
	coupleId: string;
	date: string;
	category: string | null;
	totalQuestions: number;
	matchCount: number;
	tier: Tier;
	questionResults: TodayResultQuestion[];
	recap: string | null;
};

export type HistoryDetailNotFound = {
	error: string;
	isToday: boolean;
	isFuture: boolean;
	myAnsweredCount: number;
	partnerAnsweredCount: number;
	totalQuestions: number;
};

export type Personalization = {
	content: string;
	date: string;
	cached: boolean;
	updatedAt: number;
};

export type PartnerAskResult = {
	question: string;
	answer: string;
	probability: number;
	supportingAnswers: Array<{
		date: string;
		category: string;
		question: string;
		answer: string;
		clerkUserId: string;
		answeredAt: number;
	}>;
	createdAt: number;
};

// PartnerQuestionRow mirrors the backend's PartnerQuestionDTO
// (see backend/internal/handlers/dto.go). The wrapper from
// GET /api/partner/questions is `{ count: number, items: PartnerQuestionRow[] }`.
export type PartnerQuestionRow = {
	id: string;
	question: string;
	answer: string;
	probability: number | null;
	supportingAnswers: string | null;
	createdAt: number;
};

// ── Daily Moods ──────────────────────────────────────────────────────
export type MoodItem = {
	id: string;
	couple_id: string;
	user_id: string;
	emoji: string;
	mood_date: string;
	updated_at: number;
	user_name?: string;
};

export type MoodTimelineItem = {
	id: string;
	user_id: string;
	user_name: string;
	emoji: string;
	mood_date: string;
	created_at: number;
	updated_at: number;
};

// ── Daily Stories ────────────────────────────────────────────────────
export type StoryItem = {
	id: string;
	couple_id: string;
	author_id: string;
	caption: string;
	entry_date: string;
	created_at: number;
	author_name?: string;
	like_count?: number;
	user_liked?: boolean;
};

// ── Journals ─────────────────────────────────────────────────────────
export type JournalItem = {
	id: string;
	couple_id: string;
	author_id: string;
	title: string;
	content?: any;
	content_html: string;
	visibility: string;
	status: string;
	entry_date: string;
	created_at: number;
	updated_at: number;
	author_name?: string;
	like_count?: number;
	comment_count?: number;
	user_liked?: boolean;
	user_emoji?: string;
};

export type JournalDetail = JournalItem & {
	content: any;
};

export type JournalComment = {
	id: string;
	journal_id: string;
	author_id: string;
	author_name: string;
	content: string;
	created_at: number;
	updated_at: number;
};

export type JournalDashboard = {
	recent: JournalItem[];
	total_this_month: number;
	total_written: number;
};

// ── Reminders ────────────────────────────────────────────────────────
export type ReminderItem = {
	id: string;
	couple_id: string;
	creator_id: string;
	title: string;
	description: string;
	reminder_date: string;
	reminder_time?: string;
	alert_before_minutes: number;
	is_recurring: boolean;
	recurring_type?: string;
	recurring_end_date?: string;
	for_partner: boolean;
	is_completed: boolean;
	created_at: number;
	updated_at: number;
	creator_name?: string;
};

export type QuizQuestion = {
	id: string;
	position: number;
	category: string;
	questionText: string;
	optionA: string;
	optionB: string;
	optionC: string;
	optionD: string;
};

export type NotificationItem = {
	id: string;
	userId: string;
	type: "quiz_reminder" | "partner_answered" | "streak_risk" | "system";
	title: string;
	body: string;
	data?: string;
	readAt?: number;
	createdAt: number;
};

export type NotificationsResponse = {
	notifications: NotificationItem[];
	total: number;
	unreadCount: number;
};

// ── Notes ────────────────────────────────────────────────────────────
export type NoteConversation = {
	id: string;
	noteType: "personal" | "partner";
	createdAt: number;
	updatedAt: number;
};

export type NoteAttachment = {
	id: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
	r2Key: string;
	width: number;
	height: number;
	duration: number;
	aiDescription?: string;
	aiTranscript?: string;
	aiStatus: string;
	createdAt: number;
};

export type NoteMessage = {
	id: string;
	content: string;
	createdAt: number;
	editedAt?: number;
	deletedAt?: number;
	attachments: NoteAttachment[];
};

async function request<T>(
	path: string,
	init: RequestInit & { token: string | null },
): Promise<T> {
	const isFormData = init.body instanceof FormData;
	const res = await fetch(path, {
		...init,
		headers: {
			...(!isFormData ? { "Content-Type": "application/json" } : {}),
			...(init.token ? { Authorization: `Bearer ${init.token}` } : {}),
			...init.headers,
		},
	});
	if (!res.ok) {
		let msg = `HTTP ${res.status}`;
		let parsedBody: unknown = undefined;
		try {
			parsedBody = await res.json();
			const body = parsedBody as { error?: string };
			if (body.error) msg = body.error;
		} catch {
			// ignore
		}
		throw new ApiError(msg, res.status, parsedBody);
	}
	return (await res.json()) as T;
}

// ── Chat types ──────────────────────────────────────────────────────

export type ChatAttachment = {
	id: string;
	messageId: string;
	uploaderId: string;
	r2Key: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
	width: number;
	height: number;
	duration: number;
	thumbKey: string;
	createdAt: number;
};

export type ChatMessage = {
	id: string;
	conversationId: string;
	senderId: string;
	content: string;
	replyToId?: string;
	createdAt: number;
	editedAt?: number;
	deletedAt?: number;
	senderName: string;
	attachments: ChatAttachment[];
	readBy?: string[];
};

export type ChatConversation = {
	conversationId: string;
};

export type ChatFileItem = {
	id: string;
	fileName: string;
	mimeType: string;
	fileSize: number;
	r2Key: string;
	direction: "sent" | "received";
	createdAt: number;
};

export type ChatFileStats = {
	total: number;
	sent: number;
	received: number;
};

// ── Expense Tracker ─────────────────────────────────────────────────────
export type ExpenseCategory = {
	id: string;
	couple_id: string;
	name: string;
	icon: string;
	color: string;
	type: "income" | "expense";
	monthly_budget: number | null;
	is_essential: boolean;
	sort_order: number;
	created_at: number;
	spent_cents?: number | null;
	remaining_cents?: number | null;
};

export type ExpenseTransaction = {
	id: string;
	couple_id: string;
	user_id: string;
	category_id: string;
	type: "income" | "expense";
	amount_cents: number;
	description: string;
	transaction_date: string;
	is_recurring: boolean;
	recurring_type: string;
	created_at: number;
	updated_at: number;
	category_name?: string;
	category_icon?: string;
	category_color?: string;
};

export type LedgerEntry = ExpenseTransaction & {
	running_balance_cents: number;
};

export type ExpenseBudget = {
	id: string;
	couple_id: string;
	month: string;
	total_income_cents: number;
	created_at: number;
	updated_at: number;
};

export type ExpenseBudgetAllocation = {
	id: string;
	budget_id: string;
	category_id: string;
	allocated_cents: number;
	note: string;
	category_name?: string;
	category_icon?: string;
	category_color?: string;
};

export type DashboardCategorySummary = {
	category_id: string;
	category_name: string;
	category_icon: string;
	category_color: string;
	spent_cents: number;
	budget_cents: number;
	remaining_cents: number;
};

export type MonthlyTrendPoint = {
	month: string;
	total_income_cents: number;
	total_expense_cents: number;
};

export type DashboardSummary = {
	month: string;
	total_income_cents: number;
	total_expense_cents: number;
	balance_cents: number;
	budget_cents: number;
	categories: DashboardCategorySummary[];
	monthly_trend: MonthlyTrendPoint[];
};

export const api = {
	me: (token: string) => request<MeResponse>("/api/me", { method: "GET", token }),
	updateProfile: (token: string, body: ProfileInput) =>
		request<MeResponse>("/api/me/profile", {
			method: "PUT",
			token,
			body: JSON.stringify(body),
		}),
	syncMe: (token: string, body: { email: string; nickname: string }) =>
		request<{ ok: true }>("/api/me/sync", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	createCouple: (token: string, opts: { nickname?: string } = {}) =>
		request<{ coupleId: string; code: string }>("/api/couple", {
			method: "POST",
			token,
			body: JSON.stringify({ nickname: opts.nickname ?? "" }),
		}),
	joinCouple: (token: string, code: string) =>
		request<{ coupleId: string; alreadyMember: boolean }>("/api/couple/join", {
			method: "POST",
			token,
			body: JSON.stringify({
				code,
			}),
		}),
	abandonCouple: (token: string) =>
		request<{ ok: true }>("/api/couple/abandon", {
			method: "POST",
			token,
		}),
	todayQuiz: (token: string) =>
		request<TodayQuiz>("/api/quiz/today", { method: "GET", token }),
	answer: (
		token: string,
		body: { questionId: string; option: "A" | "B" | "C" | "D" },
	) =>
		request<{
			updated: true;
			bothAnswered: boolean;
			date: string;
			coupleId: string;
		}>("/api/quiz/answer", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	todayResult: (token: string) =>
		request<TodayResult>("/api/quiz/result/today", { method: "GET", token }),
	history: (token: string, limit = 10, offset = 0) =>
		request<{ results: HistoryEntry[]; count: number }>(
			`/api/quiz/history?limit=${limit}&offset=${offset}`,
			{
				method: "GET",
				token,
			},
		),
	historyDetail: (token: string, date: string) =>
		request<HistoryDetail>(`/api/quiz/result/${date}`, {
			method: "GET",
			token,
		}),
	personalization: (token: string) =>
		request<Personalization>("/api/personalization/today", {
			method: "GET",
			token,
		}),
	partnerAvailability: (token: string) =>
		request<{ available: boolean }>("/api/partner/availability", {
			method: "GET",
			token,
		}),
	partnerAsk: (token: string, question: string) =>
		request<PartnerAskResult>("/api/partner/ask", {
			method: "POST",
			token,
			body: JSON.stringify({ question }),
		}),
	partnerQuestions: (token: string, limit = 30) =>
		request<{ count: number; items: PartnerQuestionRow[] }>(
			`/api/partner/questions?limit=${limit}`,
			{ method: "GET", token },
		),
	streak: (token: string, opts?: { from?: string; to?: string; days?: number }) => {
		const params = new URLSearchParams();
		if (opts?.from && opts?.to) {
			params.set("from", opts.from);
			params.set("to", opts.to);
		} else if (opts?.days !== undefined) {
			params.set("days", String(opts.days));
		}
		const qs = params.toString();
		return request<Streak>(`/api/streak${qs ? `?${qs}` : ""}`, {
			method: "GET",
			token,
		});
	},
	remindPartner: (token: string) =>
		request<{ sent: boolean }>("/api/quiz/remind", {
			method: "POST",
			token,
		}),
	notifications: (token: string) =>
		request<NotificationsResponse>("/api/notifications", {
			method: "GET",
			token,
		}),
	unreadCount: (token: string) =>
		request<{ unreadCount: number }>("/api/notifications/unread", {
			method: "GET",
			token,
		}),
	markNotificationRead: (token: string, id: string) =>
		request<{ status: string }>("/api/notifications/read", {
			method: "POST",
			token,
			body: JSON.stringify({ id }),
		}),
	markAllNotificationsRead: (token: string) =>
		request<{ status: string }>("/api/notifications/read-all", {
			method: "POST",
			token,
		}),
	vapidPublicKey: (token: string) =>
		request<{ publicKey: string }>("/api/push/vapid-public-key", {
			method: "GET",
			token,
		}),
	pushSubscribe: (token: string, body: { endpoint: string; p256dh: string; auth: string }) =>
		request<{ status: string }>("/api/push/subscribe", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	pushUnsubscribe: (token: string, endpoint: string) =>
		request<{ status: string }>("/api/push/unsubscribe", {
			method: "POST",
			token,
			body: JSON.stringify({ endpoint }),
		}),

	// ── Chat ────────────────────────────────────────────────────────
	chatConversation: (token: string) =>
		request<ChatConversation>("/api/chat/conversation", {
			method: "GET",
			token,
		}),
	chatMessages: (token: string, conversationId: string, cursor?: string, limit = 50) => {
		const params = new URLSearchParams({ conversationId, limit: String(limit) });
		if (cursor) params.set("cursor", cursor);
		return request<{ messages: ChatMessage[]; nextCursor: string | null }>(
			`/api/chat/messages?${params}`,
			{ method: "GET", token },
		);
	},
	chatSend: (token: string, body: { content: string; replyToId?: string; attachmentIds?: string[] }) =>
		request<ChatMessage>("/api/chat/messages", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	chatDelete: (token: string, messageId: string) =>
		request<{ status: string }>(`/api/chat/messages/${messageId}`, {
			method: "DELETE",
			token,
		}),
	chatEdit: (token: string, messageId: string, content: string) =>
		request<ChatMessage>(`/api/chat/messages/${messageId}`, {
			method: "PATCH",
			token,
			body: JSON.stringify({ content }),
		}),
	chatMarkRead: (token: string, conversationId: string, lastMessageId: string) =>
		request<{ status: string }>("/api/chat/read", {
			method: "POST",
			token,
			body: JSON.stringify({ conversationId, lastMessageId }),
		}),
	chatUnread: (token: string) =>
		request<{ unreadCount: number }>("/api/chat/unread", {
			method: "GET",
			token,
		}),
	chatUnreadConversation: (token: string, conversationId: string) =>
		request<{ unreadCount: number }>(`/api/chat/unread/${conversationId}`, {
			method: "GET",
			token,
		}),
	chatTyping: (token: string, conversationId: string, isTyping: boolean) =>
		request<{ status: string }>("/api/chat/typing", {
			method: "POST",
			token,
			body: JSON.stringify({ conversationId, isTyping }),
		}),
	chatUploadUrl: (token: string, body: { conversationId: string; filename: string; mimeType: string; fileSize: number }) =>
		request<{ uploadUrl: string; r2Key: string; expiresAt: number }>("/api/chat/attachments/upload-url", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	chatUploadFile: (token: string, conversationId: string, file: File) => {
		const formData = new FormData();
		formData.append("conversationId", conversationId);
		formData.append("file", file);
		return request<ChatAttachment>("/api/chat/attachments/upload", {
			method: "POST",
			token,
			body: formData,
		});
	},
	chatConfirmAttachment: (token: string, body: { conversationId: string; r2Key: string; filename: string; mimeType: string; fileSize: number }) =>
		request<ChatAttachment>("/api/chat/attachments", {
			method: "POST",
			token,
			body: JSON.stringify(body),
		}),
	chatDownloadUrl: (token: string, attachmentId: string) =>
		request<{ url: string; expiresAt: number }>(`/api/chat/attachments/${attachmentId}/download`, {
			method: "GET",
			token,
		}),
	chatFiles: (token: string, opts?: { direction?: string; mimeCategory?: string; cursor?: string; limit?: number }) => {
		const params = new URLSearchParams();
		if (opts?.direction) params.set("direction", opts.direction);
		if (opts?.mimeCategory) params.set("mimeCategory", opts.mimeCategory);
		if (opts?.cursor) params.set("cursor", opts.cursor);
		if (opts?.limit) params.set("limit", String(opts.limit));
		const qs = params.toString();
		return request<{ files: ChatFileItem[]; nextCursor: string | null }>(
			`/api/chat/files${qs ? `?${qs}` : ""}`,
			{ method: "GET", token },
		);
	},
	chatFileStats: (token: string) =>
		request<ChatFileStats>("/api/chat/files/stats", {
			method: "GET",
			token,
		}),
	chatGetSettings: (token: string) =>
		request<{ userId: string; showOnlineStatus: boolean; showLastSeen: boolean; showReadReceipts: boolean }>(
			"/api/chat/settings", { method: "GET", token }
		),
	chatUpdateSettings: (token: string, settings: { showOnlineStatus?: boolean; showLastSeen?: boolean; showReadReceipts?: boolean }) =>
		request<{ userId: string; showOnlineStatus: boolean; showLastSeen: boolean; showReadReceipts: boolean }>(
			"/api/chat/settings", { method: "PUT", token, body: JSON.stringify(settings) }
		),
	chatGetPresence: (token: string, userId: string) =>
		request<{ userId: string; lastSeenAt: number; isOnline: boolean }>(
			`/api/chat/presence/${encodeURIComponent(userId)}`, { method: "GET", token }
		),
	chatPresenceHeartbeat: (token: string) =>
		request<{ status: string }>("/api/chat/presence/heartbeat", { method: "POST", token }),
	chatVerifyPin: (token: string, pin: string) =>
		request<{ valid: boolean }>("/api/chat/pin/verify", {
			method: "POST", token, body: JSON.stringify({ pin }),
		}),
	chatSetPin: (token: string, pin: string) =>
		request<{ set: boolean }>("/api/chat/pin/set", {
			method: "POST", token, body: JSON.stringify({ pin }),
		}),
	chatRemovePin: (token: string, pin: string) =>
		request<{ removed: boolean }>("/api/chat/pin/remove", {
			method: "POST", token, body: JSON.stringify({ pin }),
		}),
	chatHasPin: (token: string) =>
		request<{ hasPin: boolean }>("/api/chat/pin/has", {
			method: "GET", token,
		}),
	chatPinStatus: (token: string) =>
		request<{ hasPin: boolean; locked: boolean; lockedUntil: number; failedAttempts: number }>(
			"/api/chat/pin/status", { method: "GET", token }
		),
	chatChangePin: (token: string, oldPin: string, newPin: string) =>
		request<{ changed: boolean }>("/api/chat/pin/change", {
			method: "POST", token, body: JSON.stringify({ oldPin, newPin }),
		}),
	chatSendPinResetOtp: (token: string) =>
		request<{ sent: boolean }>("/api/chat/pin/send-otp", {
			method: "POST", token,
		}),
	chatVerifyPinResetOtp: (token: string, otp: string) =>
		request<{ verified: boolean }>("/api/chat/pin/verify-otp", {
			method: "POST", token, body: JSON.stringify({ otp }),
		}),
	chatResetPinWithOtp: (token: string, otp: string, pin: string) =>
		request<{ reset: boolean }>("/api/chat/pin/reset-with-otp", {
			method: "POST", token, body: JSON.stringify({ otp, pin }),
		}),
	callToken: (token: string) =>
		request<{ token: string; url: string }>("/api/call/token", {
			method: "POST", token,
		}),
	callEnd: (token: string) =>
		request<{ ended: boolean }>("/api/call/end", {
			method: "POST", token,
		}),

	// ── NOBAR ────────────────────────────────────────────────────────
	nobarListPlans: (token: string) =>
		request<{ plans: any[] }>("/api/plans", { method: "GET", token }),
	nobarQuota: (token: string) =>
		request<any>("/api/couple/quota", { method: "GET", token }),
	nobarListVideos: (token: string) =>
		request<{ videos: any[] }>("/api/videos", { method: "GET", token }),
	nobarGetVideo: (token: string, id: string) =>
		request<any>(`/api/videos/${id}`, { method: "GET", token }),
	nobarDeleteVideo: (token: string, id: string) =>
		request<{ deleted: boolean }>(`/api/videos/${id}`, { method: "DELETE", token }),
	nobarUpdateVideo: (token: string, id: string, body: { title?: string; description?: string }) =>
		request<any>(`/api/videos/${id}`, { method: "PATCH", token, body: JSON.stringify(body) }),
	nobarAddYouTube: (token: string, body: { url: string; title: string }) =>
		request<any>("/api/videos/youtube", { method: "POST", token, body: JSON.stringify(body) }),
	nobarInitUpload: (token: string, body: { file_name: string; mime_type: string; file_size_bytes: number; title: string }) =>
		request<any>("/api/uploads/bunny/init", { method: "POST", token, body: JSON.stringify(body) }),
	nobarCompleteUpload: (token: string, body: { video_id: string; reservation_id: string }) =>
		request<any>("/api/uploads/bunny/complete", { method: "POST", token, body: JSON.stringify(body) }),
	nobarCancelUpload: (token: string, body: { video_id: string }) =>
		request<any>("/api/uploads/bunny/cancel", { method: "POST", token, body: JSON.stringify(body) }),

	// ── Daily Moods ──────────────────────────────────────────────────
	setTodayMood: (token: string, emoji: string) =>
		request<{ id: string; emoji: string; mood_date: string; updated_at: number }>(
			"/api/moods/today", { method: "PUT", token, body: JSON.stringify({ emoji }) },
		),
	getTodayMood: (token: string) =>
		request<{ me: MoodItem | null; partner: MoodItem | null }>("/api/moods/today", { method: "GET", token }),
	listMoods: (token: string, cursor?: string) => {
		const params = new URLSearchParams();
		if (cursor) params.set("cursor", cursor);
		const qs = params.toString();
		return request<{ moods: MoodTimelineItem[]; next_cursor?: string }>(
			`/api/moods${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},

	// ── Daily Stories ────────────────────────────────────────────────
	uploadStory: (token: string, file: File, caption: string) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("caption", caption);
		return request<StoryItem>("/api/stories", { method: "POST", token, body: formData });
	},
	listStories: (token: string, cursor?: string) => {
		const params = new URLSearchParams();
		if (cursor) params.set("cursor", cursor);
		const qs = params.toString();
		return request<{ stories: StoryItem[]; next_cursor?: string }>(
			`/api/stories${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},
	todayStories: (token: string) =>
		request<{ stories: StoryItem[]; count: number }>("/api/stories/today", { method: "GET", token }),
	toggleStoryLike: (token: string, storyId: string) =>
		request<{ liked: boolean; like_count: number }>(`/api/stories/${storyId}/like`, {
			method: "POST", token,
		}),
	getStoryLikes: (token: string, storyId: string) =>
		request<{ likes: any[] }>(`/api/stories/${storyId}/likes`, { method: "GET", token }),

	// ── Journals ────────────────────────────────────────────────────
	listJournals: (token: string, params?: { tab?: string; page?: number }) => {
		const p = new URLSearchParams();
		if (params?.tab) p.set("tab", params.tab);
		if (params?.page) p.set("page", String(params.page));
		const qs = p.toString();
		return request<{ journals: JournalItem[]; total: number }>(
			`/api/journals${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},
	createJournal: (token: string, data: {
		title: string; content: any; content_html: string;
		visibility: string; entry_date?: string; status?: string;
	}) =>
		request<JournalItem>("/api/journals", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	getJournal: (token: string, id: string) =>
		request<JournalDetail>(`/api/journals/${id}`, { method: "GET", token }),
	updateJournal: (token: string, id: string, data: {
		title: string; content: any; content_html: string;
		visibility: string; status?: string;
	}) =>
		request<JournalItem>(`/api/journals/${id}`, {
			method: "PUT", token, body: JSON.stringify(data),
		}),
	deleteJournal: (token: string, id: string) =>
		request<{ status: string }>(`/api/journals/${id}`, { method: "DELETE", token }),
	autoSaveJournal: (token: string, data: {
		id?: string; title: string; content: any; content_html: string;
		visibility: string; entry_date: string;
	}) =>
		request<{ id: string; status: string }>("/api/journals/auto-save", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	calendarJournals: (token: string, month: string) =>
		request<{ month: string; entries: Record<string, { count: number; authorIds: string[] }> }>(
			`/api/journals/calendar?month=${month}`, { method: "GET", token },
		),
	dashboardJournals: (token: string) =>
		request<JournalDashboard>("/api/journals/dashboard", { method: "GET", token }),
	uploadJournalMedia: (token: string, journalId: string, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return request<{ id: string; url: string; fileName: string; mimeType: string }>(
			`/api/journals/${journalId}/media`, { method: "POST", token, body: formData },
		);
	},
	toggleJournalLike: (token: string, journalId: string, emoji?: string) =>
		request<{ liked: boolean; emoji: string }>(`/api/journals/${journalId}/like`, {
			method: "POST", token, body: JSON.stringify({ emoji }),
		}),
	getJournalLikes: (token: string, journalId: string) =>
		request<{ likes: any[] }>(`/api/journals/${journalId}/likes`, { method: "GET", token }),
	listJournalComments: (token: string, journalId: string) =>
		request<{ comments: JournalComment[] }>(`/api/journals/${journalId}/comments`, { method: "GET", token }),
	createJournalComment: (token: string, journalId: string, content: string) =>
		request<JournalComment>(`/api/journals/${journalId}/comments`, {
			method: "POST", token, body: JSON.stringify({ content }),
		}),
	deleteJournalComment: (token: string, journalId: string, commentId: string) =>
		request<{ status: string }>(`/api/journals/${journalId}/comments/${commentId}`, {
			method: "DELETE", token,
		}),

	// ── Reminders ───────────────────────────────────────────────────
	listReminders: (token: string, params?: { from_date?: string; to_date?: string; page?: number; limit?: number }) => {
		const p = new URLSearchParams();
		if (params?.from_date) p.set("from_date", params.from_date);
		if (params?.to_date) p.set("to_date", params.to_date);
		if (params?.page) p.set("page", String(params.page));
		if (params?.limit) p.set("limit", String(params.limit));
		const qs = p.toString();
		return request<{ reminders: ReminderItem[] }>(
			`/api/reminders${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},
	createReminder: (token: string, data: {
		title: string; description?: string; reminder_date: string;
		reminder_time?: string; alert_before_minutes?: number;
		is_recurring?: boolean; recurring_type?: string;
		recurring_end_date?: string; for_partner?: boolean;
	}) =>
		request<ReminderItem>("/api/reminders", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	getReminder: (token: string, id: string) =>
		request<ReminderItem>(`/api/reminders/${id}`, { method: "GET", token }),
	updateReminder: (token: string, id: string, data: Partial<{
		title: string; description: string; reminder_date: string;
		reminder_time: string; alert_before_minutes: number;
		is_recurring: boolean; recurring_type: string;
		recurring_end_date: string; for_partner: boolean;
	}>) =>
		request<ReminderItem>(`/api/reminders/${id}`, {
			method: "PUT", token, body: JSON.stringify(data),
		}),
	deleteReminder: (token: string, id: string) =>
		request<{ status: string }>(`/api/reminders/${id}`, { method: "DELETE", token }),
	completeReminder: (token: string, id: string) =>
		request<{ completed: boolean }>(`/api/reminders/${id}/complete`, { method: "PATCH", token }),
	calendarReminders: (token: string, month: string) =>
		request<{ month: string; days: Record<string, { count: number }> }>(
			`/api/reminders/calendar?month=${month}`, { method: "GET", token },
		),
	todayReminders: (token: string) =>
		request<{ reminders: ReminderItem[]; count: number }>("/api/reminders/today", { method: "GET", token }),
	remindersByDate: (token: string, date: string) =>
		request<{ date: string; reminders: ReminderItem[] }>(
			`/api/reminders/date/${date}`, { method: "GET", token },
		),

	// Premium subscription.
	premiumStatus: (token: string) =>
		request<{
			isPremium: boolean; planCode: string;
			expiresAt?: number;
			devMode: boolean;
			priceMonthly: number;
			priceAnnual: number;
			limits: Record<string, number | boolean>;
			usage: Record<string, number>;
		}>("/api/premium/status", { method: "GET", token }),
	premiumCheckout: (token: string, data: { planType: string }) =>
		request<{
			transactionId: string; amount: number;
			planType: string; qrUrl: string;
			expiresAt?: string; status: string;
		}>("/api/premium/checkout", { method: "POST", token, body: JSON.stringify(data) }),
	premiumUsage: (token: string) =>
		request<{ date: string; usage: Record<string, number> }>(
			"/api/premium/usage", { method: "GET", token }),

	// ── Referral / Affiliate ──────────────────────────────────────────
	referralDashboard: (token: string) =>
		request<{
			totalClicks: number; totalSignups: number; totalPremium: number;
			link?: { code: string; url: string; createdAt: string };
			shareUrl?: string;
			balance?: { available: number; pending: number; withdrawn: number };
			recentReferrals?: any[];
		}>("/api/referral/dashboard", { method: "GET", token }),
	referralLink: (token: string) =>
		request<{ code: string; url: string; createdAt: string }>(
			"/api/referral/link", { method: "GET", token }),
	referralList: (token: string, filter = "all") =>
		request<{ referrals: any[] }>(
			`/api/referral/referrals?filter=${filter}`, { method: "GET", token }),
	referralLedger: (token: string, limit = 50, offset = 0) =>
		request<{ entries: any[]; balance: any }>(
			`/api/referral/ledger?limit=${limit}&offset=${offset}`, { method: "GET", token }),
	referralBankAccounts: (token: string) =>
		request<{ accounts: any[] }>("/api/referral/bank-accounts", { method: "GET", token }),
	referralAddBank: (token: string, data: { bankName: string; accountName: string; accountNumber: string; isDefault: boolean }) =>
		request<any>("/api/referral/bank-accounts", { method: "POST", token, body: JSON.stringify(data) }),
	referralDeleteBank: (token: string, id: string) =>
		request<any>(`/api/referral/bank-accounts/${id}`, { method: "DELETE", token }),
	referralWithdraw: (token: string, data: { bankAccountId: string; amountCents: number }) =>
		request<any>("/api/referral/withdraw", { method: "POST", token, body: JSON.stringify(data) }),
	referralWithdrawals: (token: string) =>
		request<{ withdrawals: any[] }>("/api/referral/withdrawals", { method: "GET", token }),

	// ── Telegram Notifications ──────────────────────────────────────
	telegramLink: (token: string) =>
		request<{ url: string; token: string }>("/api/telegram/link", {
			method: "POST", token,
		}),
	telegramStatus: (token: string) =>
		request<{ connected: boolean; username: string }>("/api/telegram/status", {
			method: "GET", token,
		}),
	telegramPreferences: (token: string) =>
		request<{ preferences: Record<string, boolean> }>("/api/telegram/preferences", {
			method: "GET", token,
		}),
	telegramUpdatePreferences: (token: string, prefs: { type: string; enabled: boolean }[]) =>
		request<{ status: string }>("/api/telegram/preferences", {
			method: "PATCH", token, body: JSON.stringify(prefs),
		}),
	telegramDisconnect: (token: string) =>
		request<{ status: string }>("/api/telegram/disconnect", {
			method: "POST", token,
		}),
	telegramTest: (token: string) =>
		request<{ status: string }>("/api/telegram/test", {
			method: "POST", token,
		}),

	// ── Notes ────────────────────────────────────────────────────────
	notesConversations: (token: string) =>
		request<{ conversations: NoteConversation[] }>("/api/notes/conversations", {
			method: "GET", token,
		}),
	notesMessages: (token: string, params: { conversationId?: string; type?: string; cursor?: string; limit?: number }) => {
		const p = new URLSearchParams();
		if (params.conversationId) p.set("conversationId", params.conversationId);
		if (params.type) p.set("type", params.type);
		if (params.cursor) p.set("cursor", params.cursor);
		if (params.limit) p.set("limit", String(params.limit));
		const qs = p.toString();
		return request<{ messages: NoteMessage[]; hasMore: boolean; nextCursor: number | null }>(
			`/api/notes/messages${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},
	notesSendMessage: (token: string, data: { content: string; conversationId?: string; type?: string }) =>
		request<NoteMessage>("/api/notes/messages", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	notesSendWithAttachment: (token: string, data: { content: string; type: string; file: File }) => {
		const formData = new FormData();
		formData.append("content", data.content);
		formData.append("type", data.type);
		formData.append("file", data.file);
		return request<NoteMessage>("/api/notes/messages/with-attachment", {
			method: "POST", token, body: formData,
		});
	},
	notesDeleteMessage: (token: string, id: string) =>
		request<{ status: string }>(`/api/notes/messages/${id}`, {
			method: "DELETE", token,
		}),
	notesEditMessage: (token: string, id: string, content: string) =>
		request<{ status: string }>(`/api/notes/messages/${id}`, {
			method: "PATCH", token, body: JSON.stringify({ content }),
		}),
	notesDownloadAttachment: (_token: string, id: string) =>
		`/api/notes/attachments/${id}/download`,
	notesAsk: (token: string, question: string, type: string) =>
		request<{ answer: string }>("/api/notes/ask", {
			method: "POST", token, body: JSON.stringify({ question, type }),
		}),
	notesCalendar: (token: string, month: string) =>
		request<{ month: string; counts: Record<string, number> }>(
			`/api/notes/calendar?month=${month}`, { method: "GET", token },
		),
	notesDay: (token: string, date: string, type: string) =>
		request<{ date: string; noteType: string; messages: NoteMessage[] }>(
			`/api/notes/day?date=${date}&type=${type}`, { method: "GET", token },
		),

	// ── Expense Tracker ──────────────────────────────────────────────
	listExpenseCategories: (token: string) =>
		request<{ categories: ExpenseCategory[] }>("/api/expense/categories", { method: "GET", token }),
	createExpenseCategory: (token: string, data: {
		name: string; icon?: string; color?: string; type?: string;
		monthly_budget?: number | null; is_essential?: boolean; sort_order?: number;
	}) =>
		request<ExpenseCategory>("/api/expense/categories", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	updateExpenseCategory: (token: string, id: string, data: {
		name: string; icon?: string; color?: string; type?: string;
		monthly_budget?: number | null; is_essential?: boolean; sort_order?: number;
	}) =>
		request<ExpenseCategory>(`/api/expense/categories/${id}`, {
			method: "PUT", token, body: JSON.stringify(data),
		}),
	deleteExpenseCategory: (token: string, id: string) =>
		request<{ status: string }>(`/api/expense/categories/${id}`, { method: "DELETE", token }),

	listExpenseTransactions: (token: string, params?: {
		category_id?: string; type?: string; from_date?: string; to_date?: string;
		search?: string; limit?: number; offset?: number;
	}) => {
		const p = new URLSearchParams();
		if (params?.category_id) p.set("category_id", params.category_id);
		if (params?.type) p.set("type", params.type);
		if (params?.from_date) p.set("from_date", params.from_date);
		if (params?.to_date) p.set("to_date", params.to_date);
		if (params?.search) p.set("search", params.search);
		if (params?.limit) p.set("limit", String(params.limit));
		if (params?.offset) p.set("offset", String(params.offset));
		const qs = p.toString();
		return request<{ transactions: ExpenseTransaction[]; total: number }>(
			`/api/expense/transactions${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},
	createExpenseTransaction: (token: string, data: {
		category_id: string; type: string; amount_cents: number;
		description?: string; transaction_date?: string;
		is_recurring?: boolean; recurring_type?: string;
	}) =>
		request<ExpenseTransaction>("/api/expense/transactions", {
			method: "POST", token, body: JSON.stringify(data),
		}),
	updateExpenseTransaction: (token: string, id: string, data: {
		category_id: string; type: string; amount_cents: number;
		description?: string; transaction_date?: string;
		is_recurring?: boolean; recurring_type?: string;
	}) =>
		request<ExpenseTransaction>(`/api/expense/transactions/${id}`, {
			method: "PUT", token, body: JSON.stringify(data),
		}),
	deleteExpenseTransaction: (token: string, id: string) =>
		request<{ status: string }>(`/api/expense/transactions/${id}`, { method: "DELETE", token }),

	expenseLedger: (token: string, params?: {
		from_date?: string; to_date?: string; category_id?: string;
		type?: string; limit?: number; offset?: number;
	}) => {
		const p = new URLSearchParams();
		if (params?.from_date) p.set("from_date", params.from_date);
		if (params?.to_date) p.set("to_date", params.to_date);
		if (params?.category_id) p.set("category_id", params.category_id);
		if (params?.type) p.set("type", params.type);
		if (params?.limit) p.set("limit", String(params.limit));
		if (params?.offset) p.set("offset", String(params.offset));
		const qs = p.toString();
		return request<{ entries: LedgerEntry[]; total: number }>(
			`/api/expense/transactions/ledger${qs ? `?${qs}` : ""}`, { method: "GET", token },
		);
	},

	exportExpenseTransactions: (token: string, format: string, params?: {
		from?: string; to?: string; type?: string;
	}) => {
		const p = new URLSearchParams({ format });
		if (params?.from) p.set("from", params.from);
		if (params?.to) p.set("to", params.to);
		if (params?.type) p.set("type", params.type);
		return request<Blob>(`/api/expense/transactions/export?${p.toString()}`, {
			method: "GET", token,
		});
	},

	getExpenseBudget: (token: string, month?: string) => {
		const p = month ? `?month=${month}` : "";
		return request<{ budget: ExpenseBudget | null; allocations: ExpenseBudgetAllocation[] }>(
			`/api/expense/budgets${p}`, { method: "GET", token },
		);
	},
	upsertExpenseBudget: (token: string, data: {
		month: string; total_income_cents: number;
		allocations: { category_id: string; allocated_cents: number; note?: string }[];
	}) =>
		request<ExpenseBudget>("/api/expense/budgets", {
			method: "POST", token, body: JSON.stringify(data),
		}),

	getExpenseDashboard: (token: string, month?: string) => {
		const p = month ? `?month=${month}` : "";
		return request<DashboardSummary>(`/api/expense/dashboard${p}`, { method: "GET", token });
	},

	// ── Share ────────────────────────────────────────────────────────
	getShareLink: (token: string, resultId: string, coupleId?: string) => {
		const p = new URLSearchParams({ resultId });
		if (coupleId) p.set("coupleId", coupleId);
		return request<{ slug: string; url: string; resultId: string }>(
			`/api/share/link?${p.toString()}`, { method: "GET", token },
		);
	},
	generateShareLink: (token: string, data: {
		resultId: string;
		utmSource?: string;
		utmMedium?: string;
		utmCampaign?: string;
		utmContent?: string;
	}) =>
		request<{ slug: string; url: string; baseURL: string }>(
			"/api/share/generate", { method: "POST", token, body: JSON.stringify(data) },
		),
};
